import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import environment from '@environments/environment';
import {
  catchError,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  from,
  map,
  mergeMap,
  Observable,
  of,
  shareReplay,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { IUser } from '../shared/user.interface';
import { LoadingService } from '../loading/loading.service';
import { Store } from '@ngrx/store';
import { PermissionsService } from '@shared/services/permissions.service';
import { OKTA_AUTH, OktaAuthStateService } from '@okta/okta-angular';
import OktaAuth, { AccessToken, Token } from '@okta/okta-auth-js';
import { setUserRoleInfo } from '@shared/store/actions/user-role.actions';
import { Router } from '@angular/router';
import {
  selectLoadingUser,
  selectProfileComplete,
  selectUser,
} from '@shared/store/selectors/user.selectors';
import { User } from '../iris-sites/interfaces/sites.interfaces';
import {
  setLoading,
  storeUserDetail,
} from '@shared/store/actions/user.actions';
import { SessionTimerService } from '../timeout/session-timer.service';
import { SESSION_TOKEN } from './custom-providers/logout-provider';
import { FullStoryService } from '@shared/services/full-story.service';
import { getUserTimezoneIdentifier } from '@shared/services/utils.service';
import { AppInsightsService, WalkmeService } from '@data-access/shared';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly _oktaAuth: OktaAuth = inject(OKTA_AUTH);
  private readonly _oktaAuthState = inject(OktaAuthStateService);
  private readonly _http = inject(HttpClient);
  private readonly _store = inject(Store);
  private readonly _permissionsService = inject(PermissionsService);
  private readonly _router = inject(Router);
  private readonly _loading = inject(LoadingService);
  private readonly _sessionTimerService = inject(SessionTimerService);
  private readonly _session: { set: () => void } = inject(SESSION_TOKEN);
  private readonly _walkme = inject(WalkmeService);
  private readonly _fullStoryService = inject(FullStoryService);
  private readonly _appInsights = inject(AppInsightsService);

  isAuthenticated$: Observable<boolean>;
  user$: Observable<User | IUser | undefined> = this._store.select(selectUser);

  loadingUser$: Observable<boolean | undefined> =
    this._store.select(selectLoadingUser);

  profileComplete$: Observable<boolean | undefined> = this._store.select(
    selectProfileComplete,
  );

  constructor() {
    this.isAuthenticated$ = this.handleAuthState();
    this._oktaAuthState.authState$.subscribe((state) => {
      if (state.isAuthenticated) {
        this._sessionTimerService.startIdleTimers();
      } else {
        this._sessionTimerService.stopIdleTimers();
      }
    });
  }

  /**
   * Get the access token from the token manager or renew it if it is expired
   * @returns The access token or undefined if the token is not found or expired
   */
  getAccessToken(): Observable<string | undefined> {
    return from(this._oktaAuth.tokenManager.get('accessToken')).pipe(
      mergeMap((token) => {
        if (token && !this._oktaAuth.tokenManager.hasExpired(token)) {
          return of(token);
        }
        return this.renewToken();
      }),
      map((token: Token | undefined) => (token as AccessToken)?.accessToken),
      catchError(() => of(undefined)),
    );
  }

  /**
   * Renew the access token
   * @returns The access token or undefined if the token is not found or expired
   */
  private renewToken(): Observable<AccessToken | undefined> {
    return from(this._oktaAuth.tokenManager.renew('accessToken')).pipe(
      map((token) => token as AccessToken),
      catchError(() => of(undefined)),
    );
  }

  addLogoutInsights(reason: string, context: unknown = {}) {
    this._appInsights.trackEvent('UserLogout', {
      reason: reason,
      timestamp: new Date().toISOString(),
      userId: this.getSessionUser()?.id,
      currentRoute: this._router.url,
      ...(typeof context === 'object' && context !== null ? context : {}),
    });
  }

  private handleAuthState(): Observable<boolean> {
    // Listen to token manager events
    this._oktaAuth.tokenManager.on('renewed', (key) => {
      console.log(`Token with key ${key} renewed`);
    });

    this._oktaAuth.tokenManager.on('expired', async (key) => {
      console.log(`Token with key ${key} expired`);

      try {
        const newToken = await this._oktaAuth.tokenManager.renew(key);
        if (newToken) {
          console.log(`Token with key ${key} successfully renewed`, newToken);
        } else {
          console.error(
            `Token with key ${key} could not be renewed; logging out.`,
          );
          // Clinsphere handles auth, we do not need to navigate to logout and explicitly destroy the session
          this.addLogoutInsights('TokenRenewalFailed', {
            error: `Token with key ${key} could not be renewed`,
          });
          this._router.navigate(['/auth/logout']);
        }
      } catch (error) {
        console.error(
          `Token renewal failed for key ${key}`,
          error,
          'logging out.',
        );
        // Clinsphere handles auth, we do not need to navigate to logout and explicitly destroy the session
        this.addLogoutInsights('TokenRenewalFailed', { error });
        this._router.navigate(['/auth/logout']);
      }
    });

    // Create and return the isAuthenticated$ observable
    return this._oktaAuthState.authState$.pipe(
      filter((state) => !!state),
      distinctUntilChanged(
        (prev, curr) => prev.isAuthenticated === curr.isAuthenticated,
      ),
      tap((state) => {
        firstValueFrom(this.loadingUser$).then((isLoading) => {
          if (state.isAuthenticated) {
            this._session.set();
          }
          if (state.isAuthenticated && !isLoading) {
            this.checkSessionUser();
          }
        });
      }),
      switchMap(() =>
        from(
          this._oktaAuth.tokenManager
            .get('accessToken')
            .then(
              (token) =>
                !!token && !this._oktaAuth.tokenManager.hasExpired(token),
            ),
        ),
      ),
      shareReplay(1),
    );
  }

  getToken() {
    return this._oktaAuth.tokenManager.getTokens();
  }

  private async checkForUser() {
    const isLoading = await firstValueFrom(this.loadingUser$);
    if (isLoading) {
      return;
    }

    this._store.dispatch(setLoading({ loading: true }));

    try {
      const idToken = await this._oktaAuth.tokenManager.get('idToken');
      const oktaUser = (idToken as { claims: { name: string; email: string } })
        ?.claims;

      let nameParts = oktaUser.name.split(' ');

      if (!nameParts.length) {
        nameParts = ['', ''];
      } else if (nameParts.length === 1) {
        nameParts = [nameParts[0], ''];
      }

      if (!oktaUser.name || !oktaUser.email) {
        const user: User = {
          id: '',
          email: oktaUser.email as string,
          is_internal: false,
          first_name: nameParts[0],
          last_name: nameParts[nameParts.length - 1],
          data: {
            isProfileComplete: false,
            isReturnUser: false,
            showWelcomeCard: false,
            showSubmissionCard: false,
          },
          role: {
            id: '',
            name: '',
          },
        };

        this._store.dispatch(
          storeUserDetail({
            user,
          }),
        );

        this._router.navigate(['/welcome']);
      } else {
        const zoneInfo = getUserTimezoneIdentifier();
        const payload = {
          first_name: nameParts[0],
          last_name: nameParts[nameParts.length - 1],
          email: oktaUser.email,
          data: {
            zoneinfo: zoneInfo,
            /*
            locale: oktaUser.locale,
            name: oktaUser.name,
            email_verified: oktaUser.email_verified,
            */
          },
        };

        this.getOrCreateUser(payload).subscribe({
          next: (user) => {
            const role = {
              name: user.role.name,
              id: user.role.id,
              is_internal: user.role.is_internal,
            };
            this._permissionsService.setRole(role);
            this._store.dispatch(setUserRoleInfo({ userRole: role }));
            // Store user BEFORE navigation so MaintenanceGuard can check it
            this._store.dispatch(storeUserDetail({ user }));
            this._loading.hideLoading();
            this.navigateToPageOrLanding();
            this._walkme.loadWalkMe();
          },
          error: (error) => {
            console.error('[Auth] Error during user creation:', error);
            this._loading.hideLoading();
          },
        });
      }
    } catch (error) {
      console.error('Error case for revoked issue:', error, 'logging out.');
      // Clinsphere handles auth, we do not need to navigate to logout and explicitly destroy the session
      this.addLogoutInsights('TokenRevokedIssue', { error });
      this._router.navigate(['/auth/logout']);
    }
  }

  navigateToPageOrLanding(): void {
    const originalUri =
      window.location.pathname !== '/auth/login/callback'
        ? window.location.pathname
        : this._oktaAuth.getOriginalUri();
    if (!originalUri || originalUri === '/') {
      this._router.navigate(['/welcome']);
    } else {
      this._router.navigate([originalUri]);
    }
  }

  getOrCreateUser(user: any) {
    return this._http
      .post<any>(`${environment.irisApi}/auth/user/get-create/`, user)
      .pipe(
        tap((fetchedUser) => {
          this._store.dispatch(storeUserDetail({ user: fetchedUser }));
          this._store.dispatch(setLoading({ loading: false }));
          this._fullStoryService.initFullStory(fetchedUser);
        }),
      );
  }

  // TODO: This does not work reliably / as expected.
  // The observable is async and the user is not available immediately.
  getSessionUser(): any {
    let storeUser: any;
    this._store
      .select('user')
      .pipe(filter((user) => !!user?.currentUser))
      .subscribe((user) => (storeUser = user.currentUser))
      .unsubscribe();
    return storeUser;
  }

  _getSessionUser() {
    return this._store.select('user').pipe(
      filter((user) => !!user?.currentUser),
      take(1),
      map((user) => user.currentUser),
    );
  }

  updateUser(updatedUser: any) {
    const user = this.getSessionUser();

    return this._http
      .put<any>(`${environment.irisApi}/auth/user/${user.id}/`, updatedUser)
      .pipe(
        tap((user) => {
          this._store.dispatch(storeUserDetail({ user }));
        }),
      );
  }

  impersonateUser(userId: string) {
    return this._http.post<any>(
      `${environment.irisApi}/auth/user/${userId}/impersonate/`,
      {},
    );
  }

  endImpersonationSesion() {
    return this._http.put<any>(
      `${environment.irisApi}/auth/user/impersonate/end/`,
      {},
    );
  }

  loadUser(userId: string) {
    return this._http
      .get<IUser>(`${environment.irisApi}/auth/user/${userId}/`)
      .pipe(tap((user) => this._store.dispatch(storeUserDetail({ user }))));
  }

  getAllUsers() {
    return this._http.get<IUser[]>(`${environment.irisApi}/auth/user/`);
  }

  updateInternalUser(userObject: any, userId: any) {
    return this._http.put<any>(
      `${environment.irisApi}/auth/user/${userId}/`,
      userObject,
    );
  }

  private checkSessionUser() {
    try {
      const sessionData = this.getSessionUser();

      if (!sessionData || !sessionData.id) {
        this.checkForUser();
        return;
      }
      const role = {
        name: sessionData.role.name,
        is_internal: sessionData.role.is_internal,
      };
      this._store.dispatch(setUserRoleInfo({ userRole: role }));
      this._store.dispatch(storeUserDetail({ user: sessionData }));
    } catch (error) {
      console.error('Error checking session user', error);
    }
  }

  async checkAuthentication(): Promise<boolean> {
    const isAuthenticated = await firstValueFrom(this.isAuthenticated$);
    if (!isAuthenticated) {
      this._oktaAuth.setOriginalUri(window.location.pathname);
    }

    return !!isAuthenticated;
  }

  /*
   * @TODO: Move the following methods to home.service.ts. These are not related to auth directly
   * */
  getInvites(user_id: string) {
    return this._http.get<any>(
      `${environment.irisApi}/auth/user/${user_id}/invites/`,
    );
  }

  getAccessRequests(user_id: string) {
    return this._http.get<any>(
      `${environment.irisApi}/auth/user/${user_id}/access/`,
    );
  }

  acceptInvite(id: string) {
    return this._http.post<any>(
      `${environment.irisApi}/accept-invite/${id}/`,
      null,
    );
  }

  declineInvite(id: string) {
    return this._http.delete<any>(`${environment.irisApi}/invite/${id}/`);
  }
}
